
import React, { useState, useEffect } from 'react';
import {
  getBookings,
  getBookingById,
  updateBooking,
  updateBookingStatus
} from '../../Services/Operations/ProductAPI';
import '../../Styles/ManagementPages.css';
import { useSelector } from 'react-redux';

const formatDateDMY = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  return `${day}.${month}.${year}`;
};

const formatTime = (timeString) => {
  if (!timeString)
    return 'N/A';
  return timeString.substring(0, 5);
};

const formatDateTime = (dateString) => {
  if (!dateString) return 'N/A';
  const date = new Date(dateString);
  const day = String(date.getDate()).padStart(2, '0');
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const year = date.getFullYear();
  const hours = String(date.getHours()).padStart(2, '0');
  const minutes = String(date.getMinutes()).padStart(2, '0');
  return `${day}.${month}.${year} ${hours}:${minutes}`;
};

const SkeletonStatsCard = () => (
  <div className="stat-card skeleton">
    <div className="skeleton-title"></div>
    <div className="skeleton-value"></div>
    <div className="skeleton-subtitle"></div>
  </div>
);

const SkeletonTableRow = () => (
  <tr className="skeleton-row">
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-cell"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-badge"></div></td>
    <td><div className="skeleton-actions"></div></td>
  </tr>
);

const SkeletonTable = () => (
  <div className="table-container skeleton">
    <table className="data-table">
      <thead>
        <tr>
          <th>PNR</th>
          <th>Passenger</th>
          <th>Route</th>
          <th>Bus</th>
          <th>Booking Date</th>
          <th>Booking Time</th>
          <th>Journey Date</th>
          <th>Journey Time</th>
          <th>Seats</th>
          <th>Amount</th>
          <th>Status</th>
          <th>Payment</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((i) => (
          <SkeletonTableRow key={i} />
        ))}
      </tbody>
    </table>
  </div>
);

const SkeletonFilterPanel = () => (
  <div className="filter-panel skeleton">
    <div className="filter-header">
      <div className="skeleton-title"></div>
      <div className="skeleton-close"></div>
    </div>
    <div className="filter-grid">
      {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12].map((i) => (
        <div key={i} className="filter-group">
          <div className="skeleton-label"></div>
          <div className="skeleton-input"></div>
        </div>
      ))}
    </div>
    <div className="filter-actions">
      <div className="skeleton-button"></div>
      <div className="skeleton-button"></div>
    </div>
  </div>
);

const SkeletonModal = () => (
  <div className="modal-overlay">
    <div className="modal-content1 skeleton-modal" style={{ maxWidth: '800px' }}>
      <div className="modal-header">
        <div className="skeleton-title"></div>
        <div className="skeleton-close"></div>
      </div>
      <div className="modal-body">
        <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
          <div>
            <div className="skeleton-subtitle"></div>
            {[1, 2, 3, 4, 5, 6].map((i) => (
              <div key={i} className="skeleton-detail-row">
                <div className="skeleton-label"></div>
                <div className="skeleton-value"></div>
              </div>
            ))}
          </div>
          <div>
            <div className="skeleton-subtitle"></div>
            {[1, 2, 3].map((i) => (
              <div key={i} className="skeleton-detail-row">
                <div className="skeleton-label"></div>
                <div className="skeleton-value"></div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <div className="modal-footer">
        <div className="skeleton-button"></div>
      </div>
    </div>
  </div>
);

const BookingManage = () => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [editingBooking, setEditingBooking] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [viewingBooking, setViewingBooking] = useState(null);
  const [error, setError] = useState(null);
  const { token } = useSelector(state => state.user);

  const [searching, setSearching] = useState(false);
  const [isFetchingDetails, setIsFetchingDetails] = useState(false);

  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalBookings, setTotalBookings] = useState(0);
  const [pageSize] = useState(10);

  const [filters, setFilters] = useState({
    pnr: '',
    passengerName: '',
    passengerEmail: '',
    passengerPhone: '',
    route: '',
    bus: '',
    bookingDateFrom: '',
    bookingDateTo: '',
    journeyDateFrom: '',
    journeyDateTo: '',
    minAmount: '',
    maxAmount: '',
    paymentStatus: '',
    paymentMethod: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [isFiltered, setIsFiltered] = useState(false);

  useEffect(() => {
    const delayDebounceFn = setTimeout(() => {
      fetchBookings();
    }, 500);

    return () => clearTimeout(delayDebounceFn);
  }, [currentPage, searchTerm, statusFilter, filters]);

  const fetchBookings = async (isInitialLoad = false) => {
  try {
    if (isInitialLoad) {
      setLoading(true);
    } else {
      setSearching(true);
    }

    const filterParams = {};

    if (searchTerm) {
      filterParams.search = searchTerm;
    }

    if (statusFilter && statusFilter !== 'All Status') {
      filterParams.status = statusFilter;
    }

    Object.keys(filters).forEach(key => {
      if (filters[key]) {
        filterParams[key] = filters[key];
      }
    });

    const params = {
      page: currentPage,
      limit: pageSize,
      ...filterParams
    };

    const response = await getBookings(token, params);
    if (response && response.success) {
      const formattedBookings = response.bookings.map(booking => ({
        id: booking.id,
        pnr: booking.pnr,
        user: booking.user?.name || 'N/A',
        userEmail: booking.user?.email || 'N/A',
        userPhone: booking.user?.phone || 'N/A',
        route: booking.route?.from && booking.route?.to
          ? `${booking.route.from} → ${booking.route.to}`
          : 'N/A',
        bus: booking.bus?.name || 'N/A',
        busNumber: booking.bus?.number || 'N/A',
        bookingDate: booking.bookedOn || booking.createdAt,
        bookingDateTime: booking.createdAt || booking.bookedOn,
        journeyDate: booking.journeyDetails?.date || booking.travelDate,
        journeyTime: booking.journeyDetails?.time || booking.travelTime,
        seats: booking.seatsCount || 0,
        seatNumbers: booking.seats?.map(s => s.seatNumber).join(', ') || 'N/A',
        amount: booking.amount || 0,
        status: booking.status || 'Pending',
        payment: booking.paymentStatus || 'Pending',
        paymentMethod: booking.paymentMethod || 'N/A',
        boardingPoint: booking.boardingPoint || {},
        droppingPoint: booking.droppingPoint || {},
        isInstantBooking: booking.isInstantBooking || false,
        rawData: booking
      }));

      setBookings(formattedBookings);
      setTotalPages(response.totalPages || 1);
      setTotalBookings(response.total || 0);

      const hasActiveFilters =
        statusFilter !== 'All Status' ||
        searchTerm !== '' ||
        Object.values(filters).some(value => value !== '');
      setIsFiltered(hasActiveFilters);
    } else {
      setError('Failed to fetch bookings.');
      setBookings([]);
    }

  } catch (err) {
    console.error('Error fetching bookings:', err);
    setError('Failed to fetch bookings. Please try again.');
    setBookings([]);
  } finally {
    setLoading(false);
    setSearching(false);
  }
};

  const fetchBookingById = async (id) => {
    try {
      const response = await getBookingById(id, token);
      return response.data || response;
    } catch (err) {
      console.error('Error fetching booking:', err);
      throw err;
    }
  };

  const handleStatusFilterChange = (e) => {
    setStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchChange = (e) => {
    setSearchTerm(e.target.value);
    setCurrentPage(1);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    setCurrentPage(1);
    fetchBookings();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const applyFilters = () => {
    setCurrentPage(1);
    fetchBookings();
    setShowFilters(false);
  };

  const clearFilters = () => {
    setFilters({
      pnr: '',
      passengerName: '',
      passengerEmail: '',
      passengerPhone: '',
      route: '',
      bus: '',
      bookingDateFrom: '',
      bookingDateTo: '',
      journeyDateFrom: '',
      journeyDateTo: '',
      minAmount: '',
      maxAmount: '',
      paymentStatus: '',
      paymentMethod: ''
    });
    setSearchTerm('');
    setStatusFilter('All Status');
    setCurrentPage(1);
    setIsFiltered(false);
  };

  const getSafeString = (value) => {
    if (value === null || value === undefined) return '';
    return String(value);
  };

  const getSafeLowerCase = (value) => {
    if (value === null || value === undefined) return '';
    return String(value).toLowerCase();
  };

  const getSafeNumber = (value) => {
    if (value === null || value === undefined) return 0;
    return Number(value);
  };

  const handleExportCSV = () => {
    if (bookings.length === 0) {
      alert('No data to export');
      return;
    }

    const headers = ['PNR', 'Passenger', 'Email', 'Phone', 'Route', 'Bus', 'Booking Date', 'Booking Time', 'Journey Date', 'Journey Time', 'Seats', 'Amount', 'Status', 'Payment Status'];
    const csvData = bookings.map(booking => [
      booking.pnr || '',
      booking.user || '',
      booking.userEmail || '',
      booking.userPhone || '',
      booking.route || '',
      booking.bus || '',
      formatDateDMY(booking.bookingDate),
      formatTime(booking.bookingDateTime),
      formatDateDMY(booking.journeyDate),
      booking.journeyTime || '',
      booking.seats || 0,
      booking.amount || 0,
      booking.status || '',
      booking.payment || ''
    ]);

    const csvContent = [
      headers.join(','),
      ...csvData.map(row => row.map(field => `"${field}"`).join(','))
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `bookings-report-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);

    alert('CSV exported successfully!');
  };

  const handleViewBooking = async (booking) => {
    try {
      setIsFetchingDetails(true);
      const bookingData = await fetchBookingById(booking.id);

      const rawBooking = booking.rawData || booking;

      setViewingBooking({
        ...booking,
        name: booking.user || 'N/A',
        email: booking.userEmail || 'N/A',
        phone: booking.userPhone || 'N/A',
        status: booking.status || 'Pending',
        paymentStatus: booking.payment || 'Pending',
        route: booking.route || 'N/A',
        routeFrom: rawBooking.rawData?.route?.from || 'N/A',
        routeTo: rawBooking.rawData?.route?.to || 'N/A',
        bus: booking.bus || 'N/A',
        busNumber: booking.busNumber || 'N/A',
        bookingDate: booking.bookingDate,
        bookingDateTime: booking.bookingDateTime,
        journeyDate: booking.journeyDate,
        journeyTime: booking.journeyTime,
        seats: booking.seats || 0,
        seatNumbers: booking.seatNumbers || 'N/A',
        amount: booking.amount || 0,
        paymentMethod: booking.paymentMethod || 'N/A',
        boardingPoint: booking.boardingPoint || {},
        droppingPoint: booking.droppingPoint || {},
        passengers: rawBooking.rawData?.seats?.map(seat => ({
          name: seat.passenger?.fullName || 'N/A',
          age: seat.passenger?.age || 'N/A',
          gender: seat.passenger?.gender || 'N/A',
          seatNumber: seat.seatNumber || 'N/A'
        })) || []
      });
    } catch (err) {
      setViewingBooking(booking);
      console.warn('Could not fetch latest booking details. Showing cached data:', err);
    } finally {
      setIsFetchingDetails(false);
    }
  };

  const handleEditBooking = (booking) => {
    setEditingBooking(booking.id);
    setEditForm({
      status: booking.status || '',
      paymentStatus: booking.payment || '',
      boardingPoint: booking.boardingPoint || {},
      droppingPoint: booking.droppingPoint || {}
    });
  };

  const handleSaveEdit = async () => {
    try {
      setError(null);

      const updateData = {
        status: editForm.status,
        paymentStatus: editForm.paymentStatus,
        boardingPoint: editForm.boardingPoint,
        droppingPoint: editForm.droppingPoint
      };

      const response = await updateBooking(editingBooking, updateData, token);

      if (response.success) {
        setBookings(prevBookings =>
          prevBookings.map(booking =>
            booking.id === editingBooking ? {
              ...booking,
              status: response.booking?.status || editForm.status || booking.status,
              payment: response.booking?.paymentStatus || editForm.paymentStatus || booking.payment,
              boardingPoint: response.booking?.boardingPoint || editForm.boardingPoint || booking.boardingPoint,
              droppingPoint: response.booking?.droppingPoint || editForm.droppingPoint || booking.droppingPoint
            } : booking
          )
        );

        setEditingBooking(null);
        setEditForm({});
        alert('Booking updated successfully!');
      } else {
        throw new Error(response.message || 'Failed to update booking');
      }
    } catch (err) {
      console.error('Error updating booking:', err);
      setError('Failed to update booking. Please try again.');
      alert('Failed to update booking. Please try again.');
    }
  };

  const handleCancelEdit = () => {
    setEditingBooking(null);
    setEditForm({});
    setError(null);
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      setError(null);

      const response = await updateBookingStatus(bookingId, { status: newStatus }, token);

      if (response.success) {
        setBookings(prevBookings =>
          prevBookings.map(booking =>
            booking.id === bookingId ? {
              ...booking,
              status: newStatus || booking.status
            } : booking
          )
        );

        alert(`Booking status updated to ${newStatus}`);
      } else {
        throw new Error(response.message || 'Failed to update booking status');
      }
    } catch (err) {
      console.error('Error updating booking status:', err);
      setError('Failed to update booking status. Please try again.');
      alert('Failed to update booking status. Please try again.');
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setEditForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const closeViewModal = () => {
    setViewingBooking(null);
  };

  const handleRefresh = () => {
    setCurrentPage(1);
    clearFilters();
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) {
      setCurrentPage(prev => prev + 1);
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      setCurrentPage(prev => prev - 1);
    }
  };

  const handlePageClick = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const toggleFilters = () => {
    setShowFilters(!showFilters);
  };

  const confirmedBookings = bookings.filter(b =>
    getSafeLowerCase(b.status) === 'confirmed'
  ).length;

  const totalRevenue = bookings
    .filter(b =>
      getSafeLowerCase(b.status) === 'confirmed' &&
      getSafeLowerCase(b.payment) === 'success'
    )
    .reduce((sum, booking) => sum + getSafeNumber(booking.amount), 0);

  const pendingBookings = bookings.filter(b =>
    getSafeLowerCase(b.status) === 'pending'
  ).length;

  if (loading && bookings.length === 0) {
    return (
      <div className="management-page">
        <div className="page-header">
          <div className="skeleton-header-title"></div>
          <div className="header-actions">
            <div className="skeleton-search"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
            <div className="skeleton-button"></div>
          </div>
        </div>

        <div className="stats-cards">
          {[1, 2, 3, 4].map((i) => (
            <SkeletonStatsCard key={i} />
          ))}
        </div>

        <SkeletonTable />

        <div className="pagination-container">
          <div className="skeleton-pagination-info"></div>
          <div className="skeleton-pagination-controls"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="management-page">
      <div className="page-header">
        <h1>Booking Management</h1>
        <div className="header-actions">
          <form onSubmit={handleSearchSubmit} className="search-form">
            <div style={{ position: 'relative' }}>
              <input
                type="text"
                placeholder="Search by PNR or route..."
                className="search-input"
                value={searchTerm}
                onChange={handleSearchChange}
                style={{ width: "300px" }}
              />
              {searching && (
                <span style={{
                  position: 'absolute', right: '10px', top: '50%',
                  transform: 'translateY(-50%)', fontSize: '12px', color: '#888'
                }}>
                  searching...
                </span>
              )}
            </div>
          </form>
          <button className="filter-btn" onClick={toggleFilters}>
            🔍 Filters
          </button>
          <button className="refresh-btn" onClick={handleRefresh}>
            Refresh
          </button>
          <button className="export-btn" onClick={handleExportCSV}>
            Export CSV
          </button>
        </div>
      </div>


      {showFilters && !loading && (
        <div className="filter-panel">
          <div className="filter-header">
            <h3>Filter Bookings</h3>
            <button className="close-filters" onClick={() => setShowFilters(false)}>
              ×
            </button>
          </div>
          <div className="filter-grid">
            <div className="filter-group">
              <label>PNR Number</label>
              <input
                type="text"
                name="pnr"
                value={filters.pnr}
                onChange={handleFilterChange}
                placeholder="Enter PNR"
              />
            </div>
            <div className="filter-group">
              <label>Passenger Name</label>
              <input
                type="text"
                name="passengerName"
                value={filters.passengerName}
                onChange={handleFilterChange}
                placeholder="Enter passenger name"
              />
            </div>
            <div className="filter-group">
              <label>Passenger Email</label>
              <input
                type="email"
                name="passengerEmail"
                value={filters.passengerEmail}
                onChange={handleFilterChange}
                placeholder="Enter email"
              />
            </div>
            <div className="filter-group">
              <label>Passenger Phone</label>
              <input
                type="text"
                name="passengerPhone"
                value={filters.passengerPhone}
                onChange={handleFilterChange}
                placeholder="Enter phone"
              />
            </div>
            <div className="filter-group">
              <label>Route</label>
              <input
                type="text"
                name="route"
                value={filters.route}
                onChange={handleFilterChange}
                placeholder="Enter route"
              />
            </div>
            <div className="filter-group">
              <label>Bus</label>
              <input
                type="text"
                name="bus"
                value={filters.bus}
                onChange={handleFilterChange}
                placeholder="Enter bus name"
              />
            </div>
            <div className="filter-group">
              <label>Booking Status</label>
              <select
                value={statusFilter}
                onChange={handleStatusFilterChange}
              >
                <option value="All Status">All Status</option>
                <option value="Confirmed">Confirmed</option>
                <option value="Pending">Pending</option>
                <option value="Cancelled">Cancelled</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Payment Status</label>
              <select
                name="paymentStatus"
                value={filters.paymentStatus}
                onChange={handleFilterChange}
              >
                <option value="">All</option>
                <option value="success">Success</option>
                <option value="pending">Pending</option>
                <option value="failed">Failed</option>
              </select>
            </div>
            <div className="filter-group">
              <label>Booking Date From</label>
              <input
                type="date"
                name="bookingDateFrom"
                value={filters.bookingDateFrom}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Booking Date To</label>
              <input
                type="date"
                name="bookingDateTo"
                value={filters.bookingDateTo}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Journey Date From</label>
              <input
                type="date"
                name="journeyDateFrom"
                value={filters.journeyDateFrom}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Journey Date To</label>
              <input
                type="date"
                name="journeyDateTo"
                value={filters.journeyDateTo}
                onChange={handleFilterChange}
              />
            </div>
            <div className="filter-group">
              <label>Min Amount</label>
              <input
                type="number"
                name="minAmount"
                value={filters.minAmount}
                onChange={handleFilterChange}
                placeholder="Minimum amount"
                min="0"
              />
            </div>
            <div className="filter-group">
              <label>Max Amount</label>
              <input
                type="number"
                name="maxAmount"
                value={filters.maxAmount}
                onChange={handleFilterChange}
                placeholder="Maximum amount"
                min="0"
              />
            </div>
          </div>
          <div className="filter-actions">
            <button className="apply-filters-btn" onClick={applyFilters}>
              Apply Filters
            </button>
            <button className="clear-filters-btn" onClick={clearFilters}>
              Clear All
            </button>
          </div>
          {isFiltered && (
            <div className="active-filters">
              <span>Active Filters:</span>
              {searchTerm && (
                <span className="filter-tag">Search: {searchTerm}</span>
              )}
              {statusFilter !== 'All Status' && (
                <span className="filter-tag">Status: {statusFilter}</span>
              )}
              {Object.entries(filters).map(([key, value]) =>
                value && (
                  <span key={key} className="filter-tag">
                    {key}: {value}
                  </span>
                )
              )}
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      <div className="stats-cards">
        <div className="stat-card">
          <h3>Total Bookings</h3>
          <p>{totalBookings || bookings.length}</p>
          <span>All bookings</span>
        </div>
        <div className="stat-card">
          <h3>Confirmed</h3>
          <p>{confirmedBookings}</p>
          <span>Confirmed bookings</span>
        </div>
        <div className="stat-card">
          <h3>Revenue</h3>
          <p>₹{totalRevenue.toLocaleString()}</p>
          <span>total revenue</span>
        </div>
        <div className="stat-card">
          <h3>Pending</h3>
          <p>{pendingBookings}</p>
          <span>awaiting confirmation</span>
        </div>
      </div>



      <div className="table-container">
  <table className="data-table">
    <thead>
      <tr>
        <th>PNR</th>
        <th>Passenger</th>
        <th>Route</th>
        <th>Bus</th>
        <th>Booking Date</th>
        <th>Journey Date</th>
        <th>Journey Time</th>
        <th>Seats</th>
        <th>Amount</th>
        <th>Booking Type</th> 
        <th>Status</th>
        <th>Payment</th>
        <th>Actions</th>
      </tr>
    </thead>
    <tbody>
      {bookings && bookings.length > 0 ? (
        bookings.map((booking) => (
          <tr key={booking.id}>
            <td className="pnr">{getSafeString(booking.pnr)}</td>
            <td>
              <div>
                <div>{getSafeString(booking.user)}</div>
                <div className="phone">{getSafeString(booking.userEmail)}</div>
              </div>
            </td>
            <td>{getSafeString(booking.route)}</td>
            <td>
              <div>
                <div><strong>{getSafeString(booking.bus) || 'N/A'}</strong></div>
                {booking.busNumber && (
                  <small className="bus-number">({getSafeString(booking.busNumber)})</small>
                )}
              </div>
            </td>
            <td>{formatDateDMY(booking.bookingDate)}</td>
            <td>{formatDateDMY(booking.journeyDate)}</td>
            <td>{getSafeString(booking.journeyTime)}</td>
            <td>{getSafeNumber(booking.seats)}</td>
            <td>₹{getSafeNumber(booking.amount).toFixed(2)}</td>
            <td>
              <span className={`booking-type-badge ${booking.isInstantBooking ? 'instant' : 'seat'}`}>
                {booking.isInstantBooking ? 'Instant Booking' : 'Seat Booking'}
              </span>
            </td>
            <td>
              {editingBooking === booking.id ? (
                <select
                  name="status"
                  value={editForm.status || ''}
                  onChange={handleInputChange}
                  className="edit-select"
                >
                  <option value="Confirmed">Confirmed</option>
                  <option value="Pending">Pending</option>
                  <option value="Cancelled">Cancelled</option>
                </select>
              ) : (
                <span className={`status-badge ${getSafeLowerCase(booking.status)}`}>
                  {getSafeString(booking.status) || "Pending"}
                </span>
              )}
            </td>
            <td>
              {editingBooking === booking.id ? (
                <select
                  name="paymentStatus"
                  value={editForm.paymentStatus || ''}
                  onChange={handleInputChange}
                  className="edit-select"
                >
                  <option value="success">Success</option>
                  <option value="pending">Pending</option>
                  <option value="failed">Failed</option>
                </select>
              ) : (
                <span className={`status-badge ${getSafeLowerCase(booking.payment)}`}>
                  {getSafeString(booking.payment) || "Pending"}
                </span>
              )}
            </td>
            <td>
              {editingBooking === booking.id ? (
                <div className="edit-actions">
                  <button className="action-btn save" onClick={handleSaveEdit}>Save</button>
                  <button className="action-btn cancel" onClick={handleCancelEdit}>Cancel</button>
                </div>
              ) : (
                <div className="action-buttons">
                  <button className="action-btn view" onClick={() => handleViewBooking(booking)}>Details</button>
                  <button className="action-btn edit" onClick={() => handleEditBooking(booking)}>Update</button>
                  {getSafeLowerCase(booking.status) === "pending" && (
                    <button
                      className="action-btn save"
                      onClick={() => handleStatusUpdate(booking.id, "Confirmed")}
                      style={{ fontSize: "10px", padding: "4px 8px" }}
                    >
                      Confirm
                    </button>
                  )}
                </div>
              )}
            </td>
          </tr>
        ))
      ) : (
        <tr>
          <td colSpan="14" className="no-data"> 
            <p>No bookings found {isFiltered ? 'matching your filters' : ''}.</p>
            {isFiltered && (
              <button className="clear-filters-link" onClick={clearFilters}>
                Clear filters to see all bookings
              </button>
            )}
          </td>
        </tr>
      )}
    </tbody>
  </table>
</div>


      <div className="pagination-container">
        <div className="pagination-info">
          Showing {bookings.length} of {totalBookings} bookings
          (Page {currentPage} of {totalPages})
          {isFiltered && <span className="filtered-indicator"> • Filtered Results</span>}
        </div>
        <div className="pagination-controls">
          <button
            className="pagination-btn"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            Previous
          </button>

          <div className="page-numbers">
            {Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
              let pageNumber;
              if (totalPages <= 5) {
                pageNumber = i + 1;
              } else if (currentPage <= 3) {
                pageNumber = i + 1;
              } else if (currentPage >= totalPages - 2) {
                pageNumber = totalPages - 4 + i;
              } else {
                pageNumber = currentPage - 2 + i;
              }

              return (
                <button
                  key={pageNumber}
                  className={`page-number ${pageNumber === currentPage ? 'active' : ''}`}
                  onClick={() => handlePageClick(pageNumber)}
                >
                  {pageNumber}
                </button>
              );
            })}
          </div>

          <button
            className="pagination-btn"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            Next
          </button>
        </div>
      </div>


      {viewingBooking && !isFetchingDetails && (
        <div className="modal-overlay" onClick={closeViewModal}>
          <div className="modal-content1" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
            <div className="modal-header">
              <h3>Booking Details - {getSafeString(viewingBooking.pnr)}</h3>
              <button className="modal-close-btn" onClick={closeViewModal}>
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="customer-details">
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                  <div>
                    <h4>Booking Information</h4>
                    <div className="detail-row">
                      <label>PNR Number:</label>
                      <span className="pnr">{getSafeString(viewingBooking.pnr)}</span>
                    </div>
                    <div className="detail-row">
                      <label>Booking Date:</label>
                      <span>{formatDateDMY(viewingBooking.bookingDate)}</span>
                    </div>
                    <div className="detail-row">
                      <label>Booking Time:</label>
                      <span>{formatTime(viewingBooking.bookingDateTime)}</span>
                    </div>
                    <div className="detail-row">
                      <label>Booking Status:</label>
                      <span className={`status-badge ${getSafeLowerCase(viewingBooking.status)}`}>
                        {getSafeString(viewingBooking.status)}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Payment Status:</label>
                      <span className={`status-badge ${getSafeLowerCase(viewingBooking.paymentStatus)}`}>
                        {getSafeString(viewingBooking.paymentStatus)}
                      </span>
                    </div>
                    <div className="detail-row">
                      <label>Payment Method:</label>
                      <span>{getSafeString(viewingBooking.paymentMethod)}</span>
                    </div>
                    <div className="detail-row">
                      <label>Total Amount:</label>
                      <span style={{ fontWeight: 'bold', color: '#28a745' }}>₹{getSafeNumber(viewingBooking.amount)}</span>
                    </div>
                  </div>

                  <div>
                    <h4>Passenger Information</h4>
                    <div className="detail-row">
                      <label>Name:</label>
                      <span>{getSafeString(viewingBooking.name)}</span>
                    </div>
                    <div className="detail-row">
                      <label>Email:</label>
                      <span>{getSafeString(viewingBooking.email)}</span>
                    </div>
                    <div className="detail-row">
                      <label>Phone:</label>
                      <span>{getSafeString(viewingBooking.phone)}</span>
                    </div>
                  </div>
                </div>

                <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                  <h4>Journey Details</h4>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                    <div>
                      <div className="detail-row">
                        <label>Route:</label>
                        <span>{getSafeString(viewingBooking.route)}</span>
                      </div>
                      <div className="detail-row">
                        <label>Journey Date:</label>
                        <span>{formatDateDMY(viewingBooking.journeyDate)}</span>
                      </div>
                      <div className="detail-row">
                        <label>Departure Time:</label>
                        <span>{getSafeString(viewingBooking.journeyTime)}</span>
                      </div>
                    </div>
                    <div>
                      <div className="detail-row">
                        <label>Bus:</label>
                        <span>{getSafeString(viewingBooking.bus)}</span>
                      </div>
                      <div className="detail-row">
                        <label>Bus Number:</label>
                        <span>{getSafeString(viewingBooking.busNumber)}</span>
                      </div>
                      <div className="detail-row">
                        <label>Total Seats:</label>
                        <span>{getSafeNumber(viewingBooking.seats)}</span>
                      </div>
                    </div>
                  </div>
                </div>

                {viewingBooking.boardingPoint && viewingBooking.boardingPoint.name && (
                  <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                    <h4>Boarding & Dropping Points</h4>
                    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '20px' }}>
                      <div>
                        <div className="detail-row">
                          <label>Boarding Point:</label>
                          <span>{getSafeString(viewingBooking.boardingPoint.name)}</span>
                        </div>
                        <div className="detail-row">
                          <label>Boarding Time:</label>
                          <span>{getSafeString(viewingBooking.boardingPoint.time)}</span>
                        </div>
                      </div>
                      <div>
                        <div className="detail-row">
                          <label>Dropping Point:</label>
                          <span>{getSafeString(viewingBooking.droppingPoint.name)}</span>
                        </div>
                        <div className="detail-row">
                          <label>Dropping Time:</label>
                          <span>{getSafeString(viewingBooking.droppingPoint.time)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                )}

                {viewingBooking.passengers && viewingBooking.passengers.length > 0 && (
                  <div style={{ marginTop: '20px', borderTop: '1px solid #eee', paddingTop: '20px' }}>
                    <h4>Passenger List</h4>
                    <div style={{ maxHeight: '200px', overflowY: 'auto' }}>
                      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr>
                            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Name</th>
                            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Age</th>
                            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Gender</th>
                            <th style={{ textAlign: 'left', padding: '8px', borderBottom: '1px solid #ddd' }}>Seat</th>
                          </tr>
                        </thead>
                        <tbody>
                          {viewingBooking.passengers.map((passenger, index) => (
                            <tr key={index}>
                              <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{passenger.name}</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{passenger.age}</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{passenger.gender}</td>
                              <td style={{ padding: '8px', borderBottom: '1px solid #eee' }}>{passenger.seatNumber}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-footer">
              <button className="modal-close-btn secondary" onClick={closeViewModal}>
                Close
              </button>
            </div>
          </div>
        </div>
      )}


      {viewingBooking && isFetchingDetails && <SkeletonModal />}
    </div>
  );
};

export default BookingManage;